import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface AuthState {
  /** JWT access token (kept in memory via Zustand; persisted only via session storage). */
  token: string | null;
  /** Unix timestamp (ms) at which the current token expires. */
  tokenExpiresAt: number | null;
  /** Client ID stored so the token can be refreshed silently. */
  clientId: string | null;
  /** Client secret stored in session storage for silent refresh. */
  clientSecret: string | null;

  setAuth: (
    token: string,
    expiresIn: number,
    clientId: string,
    clientSecret: string,
  ) => void;
  clearAuth: () => void;
  isTokenValid: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      tokenExpiresAt: null,
      clientId: null,
      clientSecret: null,

      setAuth(token, expiresIn, clientId, clientSecret) {
        set({
          token,
          tokenExpiresAt: Date.now() + expiresIn * 1000,
          clientId,
          clientSecret,
        });
      },

      clearAuth() {
        set({
          token: null,
          tokenExpiresAt: null,
          clientId: null,
          clientSecret: null,
        });
      },

      /** True if a token exists and has more than 30 s of lifetime left. */
      isTokenValid() {
        const { token, tokenExpiresAt } = get();
        return !!token && !!tokenExpiresAt && Date.now() < tokenExpiresAt - 30_000;
      },
    }),
    {
      name: 'ep-auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
